import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import PortalLayout from '../../admin-shared/PortalLayout';
import { applyThemeToDocument, getStoredTheme } from '../../partner/utils/theme';
import api, { REQUIRED_API_VERSION, SALON_NAME, setBranchFilterActive } from '../services/api';
import { BranchPicker, useEngagementScope } from '../context/EngagementScope';
import useEngagementCounts from '../hooks/useEngagementCounts';
import { engagementBranchFilterOn, engagementPath, getEngagementBase } from '../engagementPaths';
import { getEngagementNavRoutes } from './navConfig';
import { getEngagementPortal } from '../engagementPortal';
import { getNaturalsAdminApiBase } from '../../admin-shared/naturalsSalonAdminApiBase';
import { PORTAL_HEADER } from '../../admin-shared/storage';
import EngagementSidebar from './EngagementSidebar';
import EngagementHeader from './EngagementHeader';
import EngagementBreadcrumb from './EngagementBreadcrumb';
import {
  EngagementBackendBanner,
} from './engagementServiceRecover';

const toastOptions = {
  style: {
    fontFamily: 'inherit',
    fontSize: 13,
    background: '#ffffff',
    color: 'var(--portal-ink, #0f172a)',
    border: '1px solid var(--portal-glass-border, #e2e8f0)',
    boxShadow: 'var(--portal-glass-shadow)',
  },
  success: { iconTheme: { primary: '#dc2626', secondary: '#ffffff' } },
  error: { iconTheme: { primary: '#c62828', secondary: '#ffffff' } },
};

function ScopeBar() {
  const { scopeType, branches, selectedIds, branchName, me, ready } = useEngagementScope();
  if (!ready) return null;
  if (scopeType === 'franchise') {
    const own = branches[0];
    return own ? <div className="de-small de-muted">Branch: {own.label || own.name}</div> : null;
  }
  if (branches.length < 2) return null;
  const showing = selectedIds.length === 1
    ? `Showing ${branchName(selectedIds[0])}`
    : selectedIds.length > 1
      ? `Showing ${selectedIds.length} branches`
      : `Showing all ${branches.length} branches`;
  return (
    <div className="de-row de-row--wrap de-row--between" style={{ gap: 10, alignItems: 'center' }}>
      <BranchPicker />
      <span className="de-small de-muted">
        {showing}
        {me?.scope_type === 'regions' ? ' in your regions' : ''}
      </span>
    </div>
  );
}

function SessionBanner({ backend }) {
  const { ready, sessionExpired } = useEngagementScope();
  if (!ready || !sessionExpired || backend !== 'ok') return null;
  return (
    <div className="portal-card portal-card--pad-sm border-red-200 bg-red-50 text-sm text-red-900">
      <strong>Your session has expired.</strong> Sign in again to see your branch&apos;s data.
    </div>
  );
}

function EngagementBody({ children, backend, onRefresh, busy, note }) {
  const { selectedIds } = useEngagementScope();
  const location = useLocation();
  const filterOn = engagementBranchFilterOn(location.pathname, location.search);
  setBranchFilterActive(filterOn);
  const main = children ?? <Outlet />;
  return (
    <div className="engagement-portal-content de-app w-full space-y-4">
      <EngagementBackendBanner state={backend} onRefresh={onRefresh} busy={busy} note={note} />
      <SessionBanner backend={backend} />
      {filterOn ? <ScopeBar /> : null}
      {/* A new branch choice reloads the page's data. */}
      <div key={selectedIds.length ? selectedIds.join(',') : 'all'}>{main}</div>
    </div>
  );
}

export default function EngagementShell({ children }) {
  const location = useLocation();
  const portal = getEngagementPortal();
  const embeddedInParentPortal = portal === 'partner' || portal === 'workspace';
  const counts = useEngagementCounts(!embeddedInParentPortal);
  const lastHandoffs = useRef(null);
  useEffect(() => {
    if (embeddedInParentPortal) return;
    const n = counts.open_handoffs || 0;
    if (lastHandoffs.current !== null && n > lastHandoffs.current) {
      toast('New human intervention request — open Human intervention.', { duration: 6000 });
    }
    lastHandoffs.current = n;
  }, [counts.open_handoffs, embeddedInParentPortal]);
  const [backend, setBackend] = useState('checking');
  const [recovering, setRecovering] = useState(false);
  const [recoverNote, setRecoverNote] = useState('');

  const checkBackend = () =>
    api
      .health()
      .then((h) => {
        const versionOk = (h?.api_version || 0) >= REQUIRED_API_VERSION;
        const productOk = h?.product === 'naturals_engagement';
        const db = String(h?.database || '').toLowerCase();
        const dbOk =
          h?.database_ok !== false && (!db || (db !== 'sqlite' && !db.includes('demo')));
        const next = versionOk && productOk && dbOk ? 'ok' : 'outdated';
        setBackend(next);
        return next;
      })
      .catch(() => {
        setBackend('down');
        return 'down';
      });

  const refreshService = async () => {
    setRecovering(true);
    setRecoverNote('');
    try {
      const res = await fetch(`${getNaturalsAdminApiBase()}/engagement/recover`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          [PORTAL_HEADER]: portal === 'partner' || portal === 'workspace' ? portal : 'workspace',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRecoverNote(data.error || 'Only a workspace administrator can refresh this service.');
        return;
      }
      setRecoverNote('Service refresh started. Reconnecting…');
      for (let i = 0; i < 8; i += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const next = await checkBackend();
        if (next === 'ok') {
          setRecoverNote('');
          return;
        }
      }
      setRecoverNote('The service is still starting. Reload this page in a moment.');
    } catch {
      setRecoverNote('The refresh could not be completed. Try again in a moment.');
    } finally {
      setRecovering(false);
    }
  };

  useEffect(() => {
    applyThemeToDocument(getStoredTheme());
  }, []);

  useEffect(() => {
    document.title = `${SALON_NAME} — Customer engagement`;
  }, []);

  useEffect(() => {
    checkBackend();
  }, []);

  const navRoutes = getEngagementNavRoutes(portal);
  const currentPage = navRoutes.find(
    (item) => item.segment && location.pathname.startsWith(engagementPath(item.segment)),
  );

  if (embeddedInParentPortal) {
    const eyebrow = portal === 'workspace' ? 'Customer engagement' : 'Operations & guests';
    const lede =
      portal === 'workspace'
        ? 'HQ control for every branch: agent settings, Instagram, general knowledge, automations, and each branch’s leads and bookings.'
        : 'WhatsApp AI, appointments, leads, your number, and your service list for this branch.';
    return (
      <>
        <Toaster position="bottom-right" toastOptions={toastOptions} />
        <div className="engagement-embedded flex flex-col flex-1 min-h-0 gap-4">
          <header className="portal-page-head">
            <p className="portal-page-head__eyebrow">{eyebrow}</p>
            <h1 className="portal-page-head__title">Customer engagement</h1>
            <p className="portal-page-head__lede">{lede}</p>
          </header>
          <EngagementBody backend={backend} onRefresh={refreshService} busy={recovering} note={recoverNote}>{children}</EngagementBody>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="bottom-right" toastOptions={toastOptions} />
      <PortalLayout
        portalId="engagement"
        Sidebar={(props) => <EngagementSidebar {...props} counts={counts} />}
        Header={(props) => <EngagementHeader {...props} pageTitle={currentPage?.label || 'Dashboard'} />}
        Breadcrumb={EngagementBreadcrumb}
      >
        <EngagementBody backend={backend} onRefresh={refreshService} busy={recovering} note={recoverNote}>{children}</EngagementBody>
      </PortalLayout>
    </>
  );
}
